import Patient from "@/services/patient.js";
import PatientLab from "@/services/patient/patient-lab.js";
import History from "@/services/patient-history";
import PatientNotes from "@/services/patient-notes";

const state = {
    patientDetails: null,
    patientLetterDetails: null,
    patientLetterContent: null,
    editMode: false,
    editDetails: null,
    editDirty: null,
    popupNotes: {
        show: false,
        list: [],
        key: 0
    },
    newDetails: null,
    newDetailsFormKey: 0, // used for re-initiate component,
    statusActive: true,
    statusInActive: false,
    modalNewLetter: {
        isOpen: false
    },
    modalNewAttachment: {
        isOpen: false,
        action: "add" // add | edit
    },
    modalEnrollInPlan: {
        isOpen: false,
        action: "add", // add | edit
        record: {}
    },
    allHistory: null,
    counts: null,
    reminders: [],
    labDetails: null,
    filterType: "all",
    wellnessPlan: null
};

const getters = {
    patientDetails: state => {
        return state.patientDetails;
    },
    patientLetterDetails: state => {
        return state.patientLetterDetails;
    },
    patientLetterContent: state => {
        return state.patientLetterContent;
    },
    patientId: state => {
        return state.patientDetails ? state.patientDetails.id : "";
    },
    editMode: state => {
        return state.editMode;
    },
    isDirty: state => {
        return state.editDirty;
    },
    editDetails: state => {
        return state.editDetails;
    },
    newDetails: state => {
        return state.newDetails;
    },
    statusActive: state => {
        return state.statusActive;
    },
    statusInActive: state => {
        return state.statusInActive;
    },
    popupKey: state => {
        return state.popupNotes.key;
    },
    popupNotes: state => {
        return state.popupNotes;
    },
    allHistory: state => {
        return state.allHistory;
    },
    counts: state => {
        return state.counts;
    },
    reminders: state => {
        return state.reminders;
    },
    labDetails: state => {
        return state.labDetails;
    },
    filterType: state => {
        return state.filterType;
    },
    wellnessPlan: state => {
        return state.wellnessPlan;
    }
};

const actions = {
    async getPatientDetails({ commit, dispatch }, payload) {
        return Patient.getDetails(payload.id, payload.encoded)
            .then(response => {
                if (payload.encoded === 0 || payload.encoded === "0") {
                    // change url from raw id to encoded id, without refreshing or reloading
                    history.replaceState(
                        {},
                        "",
                        `/view/patient/profile/${response.resources.encoded_id}`
                    );
                }

                if (response.message.code == 200) {
                    commit("setPatientDetails", response.resources);
                    commit("setEditDetails", _.cloneDeep(response.resources));
                    dispatch("loadPopupNotes", response.resources.id);

                    return response.resources;
                }

                return false;
            })
            .catch(error => error.data);
    },
    async getDirectUrlPatientDetails({ commit, dispatch }, payload) {
        return Patient.getDirectUrlDetails(
            payload.encodedBusinessId,
            payload.index
        )
            .then(response => {
                if (payload.encoded === 0 || payload.encoded === "0") {
                    // change url from raw id to encoded id, without refreshing or reloading
                    history.replaceState(
                        {},
                        "",
                        `/view/patient/profile/${response.resources.encoded_id}`
                    );
                }

                if (response.message.code == 200) {
                    commit("setPatientDetails", response.resources);
                    commit("setEditDetails", _.cloneDeep(response.resources));
                    dispatch("loadPopupNotes", response.resources.id);

                    return response.resources;
                }

                return false;
            })
            .catch(error => error.data);
    },
    async getPatientLetterDetails({ commit, dispatch }, payload) {
        return Patient.getPatientLetterDetails(payload)
            .then(response => {
                if (response.message.code == 200) {
                    commit("setPatientLetterDetails", response.resources);
                    return response.resources;
                }

                return false;
            })
            .catch(error => error.data);
    },
    async getPatientLetterContent({ commit, dispatch }, payload) {
        return Patient.getPatientLetterContent(payload)
            .then(response => {
                if (response.message.code == 200) {
                    commit("setPatientLetterContent", response.resources);
                    return response.resources;
                }

                return false;
            })
            .catch(error => error.data);
    },
    async loadPopupNotes({ commit }, patientId) {
        const res = await PatientNotes.getPopupNotes(patientId);

        // sort by index
        let list = res.resource.sort((a, b) => a.index - b.index);

        if (res.message.code === 200 && list.length > 0) {
            commit("showPopupNotes", list);
        } else {
            commit("clearPopupNotes"); // hide and clear list
        }
    },
    refreshSortPopupNotes({ commit }, list = []) {
        if (list.length > 0) {
            commit("showPopupNotes", list);
        }
    },
    async getHistoryCount({ commit }, payload) {
        return History.getHistoryCount(payload.patientId)
            .then(response => {
                if (response.message.code === 200) {
                    commit("setCounts", response.meta.count);
                }
            })
            .catch(error => error.data);
    },
    async getReminders({ commit }, patientId) {
        return Patient.getReminderSummary(patientId)
            .then(response => {
                if (response.message.code === 200) {
                    commit("setReminders", response.resources);
                }

                return response;
            })
            .catch(error => error.data);
    },
    async getLatestVital({ commit }, patientId) {
        return Patient.getLatestVital(patientId)
            .then(response => {
                let vitals = [];
                if (
                    response &&
                    response.message &&
                    response.message.code === 200
                ) {
                    // cannot call helper from here
                    // the caller must change the date from carbon -> luxon if want to use it
                    vitals = response.resources.map(element => ({
                        value: element.value,
                        type: element.master_vital_id,
                        uom: element.uom_unit,
                        uom_id: element.master_uom_id,
                        updatedAtCarbon: element.update_at,
                        dateCarbon: element.date
                    }));

                    this.commit("patients", {
                        id: patientId,
                        path: "vitals",
                        value: vitals
                    });
                }
                return vitals;
            })
            .catch(error => error.data);
    },
    async getLabDetails({ commit }, labId) {
        return PatientLab.getLabDetails(labId)
            .then(response => {
                if (response.code == 200) {
                    commit("setLabDetails", response.resource);

                    return response.resource;
                }

                return null;
            })
            .catch(error => error.data);
    },
    clearDetails({ commit }) {
        commit("setPatientDetails", null);
        commit("setPatientLetterDetails", null);
        commit("setReminders", []);
        commit("wellnessPlan", null);
    },
    updateFilterType({ commit }, filterType) {
        commit("setFilterType", filterType);
    }
};

const mutations = {
    setPatientDetails(state, patientDetails) {
        state.patientDetails = patientDetails;

        state.statusActive = patientDetails
            ? Number(patientDetails.status) === 1
            : true;

        state.statusInActive = patientDetails
            ? Number(patientDetails.status) === 2
            : false;
    },
    setPatientLetterDetails(state, patientLetterDetails) {
        state.patientLetterDetails = patientLetterDetails;
    },
    setPatientLetterContent(state, patientLetterContent) {
        state.patientLetterContent = patientLetterContent;
    },
    setEditMode(state, editMode) {
        state.editMode = editMode;
    },
    setDirty(state, isDirty) {
        state.editDirty = isDirty;
    },
    setEditDetails(state, editDetails) {
        state.editDetails = editDetails;
    },
    setEditDetailsField(state, field) {
        Vue.set(state.editDetails, field.key, field.value);
    },
    showPopupNotes(state, list) {
        state.popupNotes.list = list;
        state.popupNotes.show = true;
    },
    clearPopupNotes(state) {
        state.popupNotes.show = false;
        state.popupNotes.list = [];
    },
    hidePopupNotes(state) {
        state.popupNotes.show = false;
    },
    setNewDetails(state, modifiedDetails) {
        state.newDetails = {
            ...state.newDetails,
            ...modifiedDetails
        };
    },
    clearNewDetails(state) {
        state.newDetails = defaultNewPatientDetails();
        state.newDetailsFormKey++;
    },
    updatePopupKey(state) {
        state.popupNotes.key++;
    },
    openModalNewLetter(state) {
        state.modalNewLetter.isOpen = true;
    },
    openModalNewAttachment(state, params = {}) {
        state.modalNewAttachment.isOpen = true;
        state.modalNewAttachment.action = params.action;
        state.modalNewAttachment.attachment = params.attachment;
        state.modalNewAttachment.module = params.module;
        state.modalNewAttachment.patient_id = params.patient_id;
        state.modalNewAttachment.record_id = params.record_id;
        state.modalNewAttachment.record_type = params.record_type;
        state.modalNewAttachment.soap_id = params.soap_id;
    },
    closeModalNewLetter(state) {
        state.modalNewLetter.isOpen = false;
    },
    closeModalNewAttachment(state) {
        state.modalNewAttachment.isOpen = false;
    },
    openModalEnrollInPlan(state, payload = {}) {
        state.modalEnrollInPlan.isOpen = true;
        state.modalEnrollInPlan.action = payload.action || "add";
        state.modalEnrollInPlan.record = payload.record || {};
    },
    closeModalEnrollInPlan(state) {
        state.modalEnrollInPlan.isOpen = false;
    },
    setCounts(state, counts) {
        state.counts = counts;
    },
    setReminders(state, reminders) {
        state.reminders = reminders;
    },
    setLabDetails(state, labDetails) {
        state.labDetails = labDetails;
    },
    setFilterType(state, filterType) {
        state.filterType = filterType;
    },
    setWellnessPlan(state, wellnessPlan) {
        state.wellnessPlan = wellnessPlan;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};

function defaultNewPatientDetails() {
    return {
        name: "",
        breed: "",
        color: "",
        sex: "",
        age: "",
        birthdate: "",
        chip: ""
        //TODO : Need to update
    };
}
